import type { QueueStore, QueueState } from './store';
import { detectInputFormat } from '@/lib/utils/mime';

/**
 * HEIC files cannot be rendered by an <img> tag on any browser except Safari,
 * so the queue's thumbnail and the size-estimate (which needs dimensions) are
 * both blank for HEIC uploads. This helper watches the store and, for every
 * HEIC item, decodes a small JPEG preview once and writes it back along with
 * the natural dimensions.
 *
 * The expensive HEIC decode is cached per File inside the heic converter, so
 * the source row and its conversion child (which share the same File) trigger
 * only a single libheif decode between them — and that same decode is reused
 * when the file is actually converted.
 *
 * Call once from main.ts after the store is created.
 */
export function startHeicPreview(store: QueueStore): void {
  /** Item ids already handled, so we don't re-decode on every store change. */
  const handled = new Set<string>();

  function onStateChange(state: QueueState): void {
    for (const item of state.items) {
      if (handled.has(item.id)) continue;
      if (detectInputFormat(item.file) !== 'heic') continue;
      if (item.previewBlob !== undefined) continue;

      handled.add(item.id);

      import('@/lib/converters/heic')
        .then(({ decodeHeicThumbnail }) => decodeHeicThumbnail(item.file))
        .then(({ blob, width, height }) => {
          store.setPreviewBlob(item.id, blob);
          if (item.originalDimensions === undefined) {
            store.setOriginalDimensions(item.id, { width, height });
          }
        })
        .catch(() => {
          // Decode failed — allow a later retry (e.g. after the user retries
          // the item) by forgetting we handled it.
          handled.delete(item.id);
        });
    }
  }

  store.subscribe(onStateChange);
  // Run once on init in case items already exist.
  onStateChange(store.getState());
}
